import type { ChronicleDeviceState } from '@/atoms/chronicle';
import { CONNECTION_STATUS, initialDeviceState } from '@/atoms/chronicle';
import type { ChronicleError, DeviceState } from '@/tauri/chronicle/types';

/**
 * Maps Rust DeviceState to frontend ChronicleDeviceState.
 * Preserves existing state values that aren't updated by the new state.
 *
 * @param rustState - The DeviceState received from the Rust plugin
 * @param currentState - The current frontend state (for preserving unchanged values)
 * @returns Updated ChronicleDeviceState
 */
export function mapDeviceState(
  rustState: DeviceState,
  currentState: ChronicleDeviceState
): ChronicleDeviceState {
  switch (rustState.type) {
    case 'disconnected':
      return {
        ...currentState,
        connectionStatus: CONNECTION_STATUS.DISCONNECTED,
        // Preserve device info for reconnection UI
        batteryLevel: null,
        rssi: null,
        scanRssi: null,
        storageTotalMb: null,
        storageFreeMb: null,
        usbModeEnabled: false,
        autoPowerOffMinutes: null,
        isCharging: null,
        recordingPreExisting: false,
        error: null,
        // Preserve sticky max-duration reason across disconnect so a reconnect
        // latch re-send does not look like a fresh transition to the toast.
        lastStopReason: currentState.lastStopReason,
      };

    case 'scanning':
      return {
        ...currentState,
        connectionStatus: CONNECTION_STATUS.SCANNING,
        error: null,
        // HAR-1311: sticky max-duration reason must survive scan (same contract
        // as disconnect). Explicit — do not rely on incidental `...currentState`.
        lastStopReason: currentState.lastStopReason,
      };

    case 'connecting':
      return {
        ...currentState,
        connectionStatus: CONNECTION_STATUS.CONNECTING,
        deviceId: rustState.deviceId,
        error: null,
      };

    case 'connected': {
      // Only reset OTA state when the device actually reconnects (firmware
      // version changed or was previously disconnected/reconnecting).
      // Preserve OTA error/status when the device stays connected — Rust
      // emits a state_changed(connected) after OTA failure to resume normal
      // ops, but the frontend still needs the error to show the toast.
      const isReconnect = currentState.connectionStatus !== CONNECTION_STATUS.CONNECTED;
      const incomingFirmware = rustState.firmwareVersion ?? currentState.firmwareVersion;
      const firmwareChanged = incomingFirmware !== currentState.firmwareVersion;
      const shouldClearOta = isReconnect || firmwareChanged;

      return {
        ...currentState,
        connectionStatus: CONNECTION_STATUS.CONNECTED,
        deviceId: rustState.deviceId,
        deviceName: rustState.deviceName ?? currentState.deviceName,
        serialNumber: rustState.serialNumber ?? currentState.serialNumber ?? null,
        // `undefined` = not yet read (preserve any earlier reading); a real 0% is kept.
        batteryLevel: rustState.batteryLevel ?? currentState.batteryLevel ?? null,
        rssi: rustState.rssi ?? currentState.rssi ?? null,
        scanRssi: rustState.scanRssi ?? currentState.scanRssi ?? null,
        firmwareVersion: rustState.firmwareVersion ?? currentState.firmwareVersion,
        usbModeEnabled: rustState.usbModeEnabled ?? currentState.usbModeEnabled,
        autoPowerOffMinutes:
          rustState.autoPowerOffMinutes ?? currentState.autoPowerOffMinutes,
        isCharging: rustState.isCharging ?? currentState.isCharging ?? null,
        storageTotalMb: rustState.storageTotalMb ?? currentState.storageTotalMb,
        storageFreeMb: rustState.storageFreeMb ?? currentState.storageFreeMb ?? null,
        recordingPreExisting: rustState.recordingPreExisting ?? false,
        supportsChunkedSessions:
          rustState.supportsChunkedSessions ??
          currentState.supportsChunkedSessions ??
          false,
        // Rust owns the sticky lifecycle (set on MAXDUR, cleared on MCU&STA).
        // Mirror verbatim — `?? null` (not `?? currentState`) so an absent
        // wire key clears the FE copy; a currentState fallback would prevent
        // clear and could re-toast on the next set.
        lastStopReason: rustState.lastStopReason ?? null,
        ...(shouldClearOta && {
          firmwareUpdateInProgress: false,
          firmwareUpdateStatus: null,
          firmwareUpdateProgress: null,
          firmwareUpdateError: null,
          selectedFirmwareFile: null,
        }),
        error: null,
      };
    }

    case 'reconnecting': {
      const incomingError = rustState.error ? mapRustError(rustState.error) : null;
      // HAR-1910 Soft-PASS: if FE already owns a radio-off modal, a watcher
      // not-sighted DEVICE_NOT_FOUND annotation must not flip the modal to
      // "Can't find your Heidi Remote". Rust `on_watcher_radio_state` also
      // prefers BluetoothDisabled when the radio probe arrives — this covers
      // the window before that probe.
      const preserveBluetoothDisabled =
        currentState.error?.code === 'BLUETOOTH_DISABLED' &&
        incomingError?.code === 'DEVICE_NOT_FOUND';
      return {
        ...currentState,
        connectionStatus: CONNECTION_STATUS.RECONNECTING,
        deviceId: rustState.deviceId,
        // Preserve existing device info during reconnect attempts. A carried
        // error is why the last attempt failed while recovery continues
        // (e.g. the radio is off) — surface it, don't discard it.
        error: preserveBluetoothDisabled ? currentState.error : incomingError,
      };
    }

    case 'recording':
      return {
        ...currentState,
        connectionStatus: CONNECTION_STATUS.CONNECTED,
        deviceId: rustState.deviceId,
        // New recording — clear any sticky max-duration reason from the prior stop.
        lastStopReason: null,
        error: null,
      };

    case 'syncing':
      return {
        ...currentState,
        connectionStatus: CONNECTION_STATUS.CONNECTED,
        deviceId: rustState.deviceId,
        error: null,
      };

    case 'error':
      return {
        ...currentState,
        connectionStatus: CONNECTION_STATUS.ERROR,
        error: mapRustError(rustState),
      };

    default:
      // Exhaustive check - TypeScript will error if we miss a case
      return currentState;
  }
}

/**
 * Maps a Rust error payload (terminal error state, or the reason carried on
 * a reconnecting state) to ChronicleError for UI display.
 */
function mapRustError(errorState: {
  code: string;
  message: string;
  recoverable: boolean;
}): ChronicleError {
  const { code, message, recoverable } = errorState;

  const errorCodeMap: Record<string, ChronicleError['code']> = {
    BLUETOOTH_OFF: 'BLUETOOTH_DISABLED',
    BLUETOOTH_DISABLED: 'BLUETOOTH_DISABLED',
    CHRONICLE_BLUETOOTH_DISABLED: 'BLUETOOTH_DISABLED',
    CHRONICLE_DEVICE_NOT_FOUND: 'DEVICE_NOT_FOUND',
    CHRONICLE_CONNECTION_ERROR: 'CONNECTION_FAILED',
    CHRONICLE_BINDING_CODE_MISMATCH: 'BINDING_CODE_MISMATCH',
    BLUETOOTH_PERMISSION_DENIED: 'BLUETOOTH_PERMISSION_DENIED',
    BLUETOOTH_PERMISSION_PENDING: 'BLUETOOTH_PERMISSION_DENIED',
    BLUETOOTH_PERMISSION_UNKNOWN: 'UNKNOWN',
    BLUETOOTH_RESTRICTED: 'BLUETOOTH_PERMISSION_DENIED',
    NO_BLE_ADAPTER_FOUND: 'BLUETOOTH_ADAPTER_NOT_FOUND',
    SCAN_TIMEOUT: 'DEVICE_NOT_FOUND',
    DEVICE_NOT_FOUND: 'DEVICE_NOT_FOUND',
    CONNECTION_TIMEOUT: 'CONNECTION_TIMEOUT',
    CONNECTION_FAILED: 'CONNECTION_FAILED',
    FIRMWARE_UPDATE_FAILED: 'FIRMWARE_UPDATE_FAILED',
  };

  const mappedCode = errorCodeMap[code] ?? 'UNKNOWN';

  return {
    code: mappedCode,
    message,
    recoverable,
    guidance: getErrorGuidance(mappedCode),
  };
}

/**
 * Returns translation key for user-friendly guidance for resolving an error.
 */
function getErrorGuidance(code: ChronicleError['code']): string | undefined {
  const guidanceMap: Record<ChronicleError['code'], string> = {
    BLUETOOTH_DISABLED: 'settings.chronicle.error.bluetoothOffDescription',
    BLUETOOTH_PERMISSION_DENIED:
      'settings.chronicle.error.bluetoothPermissionDescription',
    BLUETOOTH_ADAPTER_NOT_FOUND: 'settings.chronicle.error.bluetoothAdapterDescription',
    DEVICE_NOT_FOUND: 'settings.chronicle.error.notFoundDescription',
    CONNECTION_TIMEOUT: 'settings.chronicle.error.connectionTimeoutDescription',
    CONNECTION_FAILED: 'settings.chronicle.error.connectionFailedDescription',
    DEVICE_BOUND_ELSEWHERE: 'settings.chronicle.error.deviceBoundElsewhereDescription',
    BINDING_CODE_MISMATCH: 'settings.chronicle.error.bindingCodeMismatchDescription',
    DEVICE_LINK_FAILED: 'settings.chronicle.error.linkFailedDescription',
    FIRMWARE_UPDATE_FAILED: 'settings.chronicle.error.firmwareUpdateFailedDescription',
    TAURI_NOT_AVAILABLE: 'settings.chronicle.error.tauriNotAvailableDescription',
    UNKNOWN: 'settings.chronicle.error.unknownDescription',
  };

  return guidanceMap[code];
}

/**
 * Creates initial device state with default values.
 * Re-exports the canonical initial state from the atom module.
 */
export function createInitialDeviceState(): ChronicleDeviceState {
  return { ...initialDeviceState };
}
